import React, { useContext } from 'react'
import { Button, Input, Popover, Spin, Tooltip } from 'antd'
import moment from 'moment'
import { StatusPill, voucherValue } from './QueueList'
import {
  statusOf, allChecks, checkLabel, sevCls, reasonLabel, alertStatusLabel, flagFields, flagLabel, flagKey,
  EVENT_META, CHANGE_KIND, inr, qtyFmt, fmtDate, fmtDateTime, isImage, isNum
} from './helpers'

// Line icons (Lucide paths, 24x24, stroke = currentColor). SVG, not emoji, so they render the same everywhere.
const ICONS = {
  flag: <React.Fragment><path d='M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z' /><line x1='4' y1='22' x2='4' y2='15' /></React.Fragment>,
  query: <React.Fragment><circle cx='12' cy='12' r='10' /><path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' /><line x1='12' y1='17' x2='12.01' y2='17' /></React.Fragment>,
  edit: <path d='M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z' />,
  check: <React.Fragment><path d='M22 11.08V12a10 10 0 1 1-5.93-9.14' /><polyline points='22 4 12 14.01 9 11.01' /></React.Fragment>,
  chevron: <polyline points='6 9 12 15 18 9' />
}
const Icon = ({ name, size }) => (
  <svg className='sax-ico' viewBox='0 0 24 24' width={size || 16} height={size || 16} fill='none' stroke='currentColor'
    strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>{ICONS[name]}</svg>
)

const Missing = ({ children }) => <span className='missing'>{children || 'Missing'}</span>

// Opens a PI in the app's read-only PI view in a separate window, reused across
// clicks so it can sit beside the audit screen. Middle/Cmd-click keeps the
// browser's normal "new tab" behaviour. No id from the server → plain text, no link.
const PI_WINDOW = 'sax-pi-view'
// Links into other modules open in a separate window, reused per kind, so they can sit beside the
// audit screen; Cmd/Ctrl/middle-click keeps the browser's own "new tab". Whether a link is shown,
// and in which mode, follows the user's role on that module (LinkAccess, set by index.js).
export const LinkAccess = React.createContext({ pi: null, spare: null, jobCard: null })
const RecordLink = ({ href, windowName, label, title }) => {
  const open = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
    // Take over the click only if the separate window really opened; if the
    // browser blocks it, the link's own target=_blank opens a new tab instead.
    const w = window.open(href, windowName, 'width=1200,height=900')
    if (w) { e.preventDefault(); w.focus() }
  }
  return (
    <a className='pilink' href={href} target='_blank' rel='noopener noreferrer' onClick={open} title={title}>
      {label} ↗
    </a>
  )
}
export const PiLink = ({ pi }) => {
  const access = useContext(LinkAccess)
  if (!pi || !pi.voucherId || !access.pi) return <span>{pi ? pi.voucherNo : ''}</span>
  return <RecordLink href={'/purchase-order/view/' + encodeURIComponent(pi.voucherId)} windowName={PI_WINDOW}
    label={pi.voucherNo} title={'Open ' + pi.voucherNo + ' in a new window'} />
}
// Spare: edit form with spare-part update permission; otherwise its read-only purchase history.
const SpareLink = ({ spare }) => {
  const access = useContext(LinkAccess)
  if (!spare || !spare._id || spare.missing || !access.spare) return <span>{spare ? spare.name : ''}</span>
  const edit = access.spare === 'edit'
  return <RecordLink href={'/spare-part/' + (edit ? 'edit/' : 'history-report/spare/') + encodeURIComponent(spare._id)}
    windowName='sax-spare' label={spare.name} title={(edit ? 'Edit ' : 'View ') + spare.name + ' in a new window'} />
}
// Job card: its report page (the app's job card view; it offers Edit/Delete only to users allowed to).
const JobCardLink = ({ jobCard }) => {
  const access = useContext(LinkAccess)
  if (!jobCard || !jobCard._id || !access.jobCard) return <span>{jobCard ? jobCard.no : ''}</span>
  return <RecordLink href={'/job-card/report/' + encodeURIComponent(jobCard._id)} windowName='sax-jobcard'
    label={jobCard.no || 'Job card'} title={'Open job card ' + (jobCard.no || '') + ' in a new window'} />
}

// Vendor invoice numbers stored as the literal strings "undefined"/"null" exist
// in live data; show them as missing, with the stored value, never as a real number.
// Pending invoice: goods received on GRN, invoice number/date entered later. That is a
// normal state, so these fields read "Pending", not a red "Missing".
const Pending = () => <span className='pending'>Pending</span>
const invoiceNo = (v, pending) => {
  const s = (v || '').trim()
  if (!s || s === 'undefined' || s === 'null') {
    return <span>{pending ? <Pending /> : <Missing />} {v ? <span className='hint'>(stored as "{v}")</span> : null}</span>
  }
  return <span className='mono'>{s}</span>
}

const NotEntered = () => <span className='notrec'>Not entered</span>

// Voucher header. The third value of a row is the flag field it shows, so a flagged field is highlighted.
const Meta = ({ item, flags }) => {
  const rows = []
  if (item.voucherType === 'PI') {
    rows.push(['Vendor', item.vendor ? item.vendor.name : <Missing />, 'VENDOR'])
    rows.push(['Vendor invoice no.', invoiceNo(item.vendorInvoiceNumber, !!item.pendingInvoice), 'INVOICE_NO'])
    rows.push(['Invoice date', fmtDate(item.invoiceDate, moment) || (item.pendingInvoice ? <Pending /> : <Missing />), 'INVOICE_DATE'])
    rows.push(['GRN date', fmtDate(item.date, moment) || <Missing />, 'GRN_DATE'])
    rows.push(['Manual GRN', item.grnNo ? <span className='mono'>{item.grnNo}</span> : <NotEntered />, 'GRN_NO'])
    rows.push(['Order no.', item.orderNumber ? <span className='mono'>{item.orderNumber}</span> : <NotEntered />, 'ORDER_NO'])
    rows.push(['Order date', fmtDate(item.orderDate, moment) || <NotEntered />, 'ORDER_DATE'])
    rows.push(['Owner', item.owner ? (item.owner.name || <Missing>Unknown owner</Missing>) : <NotEntered />, 'OWNER'])
  } else {
    const jc = item.jobCard
    rows.push(['Job card', jc ? <span className='mono'><JobCardLink jobCard={jc} /></span> : <Missing />, 'JOB_CARD'])
    rows.push(['Asset', jc && jc.asset ? <span><span className='mono'>{jc.asset.assetCode}</span>{jc.asset.model ? ' · ' + jc.asset.model : ''}</span> : <Missing />])
    rows.push(['Issue date', fmtDate(item.date, moment) || <Missing />, 'ISSUE_DATE'])
  }
  rows.push(['Store', [item.location && item.location.name, item.client && item.client.name].filter(Boolean).join(' · ') || <Missing />])
  rows.push(['Entered by', item.createdBy
    ? <span>{item.createdBy.name} <span className='hint'>{fmtDateTime(item.createdAt, moment)}</span></span>
    : <Tooltip title='This voucher does not record who entered it'><span className='notrec'>Not recorded</span></Tooltip>])
  if (item.updatedAt && item.createdAt && item.updatedAt !== item.createdAt) {
    rows.push(['Last edited', fmtDateTime(item.updatedAt, moment)])
  }
  return (
    <dl className='meta'>
      {rows.map(([k, v, f]) => (
        <div key={k} className={f && flags[flagKey(null, f)] ? 'flagged' : ''}><dt>{k}{f && flags[flagKey(null, f)] ? <span className='dt-flag' title='Flagged'><Icon name='flag' size={11} /></span> : null}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  )
}

// Status card shell: tinted icon badge, title + pill, a muted meta line, optional body and action.
const StatusCard = ({ tone, icon, title, pill, meta, action, children }) => (
  <div className={'scard ' + tone}>
    <span className='scard-ico' aria-hidden='true'>{icon}</span>
    <div className='scard-main'>
      <div className='scard-title'><b>{title}</b>{pill}</div>
      {meta && meta.length > 0 && <div className='scard-meta'>{meta.filter(Boolean).map((m, i) => <span key={i}>{m}</span>)}</div>}
      {children}
    </div>
    {action && <div className='scard-act'>{action}</div>}
  </div>
)
const Note = ({ tone, children }) => <div className={'scard-note ' + (tone || '')}>{children}</div>

const Banner = ({ item, detail }) => {
  const s = statusOf(item)
  const a = item.audit
  const by = (when) => fmtDateTime(when, moment) + (a.decidedBy ? ' by ' + a.decidedBy.name : '')
  if (s === 'CHANGED') {
    const changes = (detail && detail.changes) || []
    return (
      <StatusCard tone='changed' icon={<Icon name='edit' size={17} />} title='Edited after the audit'
        pill={<span className='spill changed'>Audit again</span>}
        meta={['Audited ' + by(a.decidedAt)]}>
        {detail
          ? (changes.length
            ? (
              <ul className='scard-changes'>
                {changes.map((c, i) => (
                  <li key={i}>
                    <span className='chg-k'>{CHANGE_KIND[c.kind] || c.kind}{c.lineNo ? ' · line ' + c.lineNo : ''}{c.field ? ' · ' + c.field : ''}</span>
                    <span className='chg-v'><s>{c.before != null ? c.before : '—'}</s> → <b>{c.after != null ? c.after : '—'}</b></span>
                  </li>))}
              </ul>)
            : <Note>The server did not list what changed.</Note>)
          : <Note>Loading what changed…</Note>}
      </StatusCard>
    )
  }
  if (s === 'QUERY') {
    const qa = a.queryAlert
    const overdue = qa && qa.dueAt && qa.open && moment(qa.dueAt).isBefore(moment())
    const pillTone = !qa ? 'query' : qa.missing ? 'bad' : qa.open === false ? 'done' : 'query'
    return (
      <StatusCard tone='query' icon={<Icon name='query' size={17} />} title={qa ? 'Audit Query ' + qa.alertNo : 'Query raised'}
        pill={<span className={'spill ' + pillTone}>{!qa ? 'Open' : qa.missing ? 'Alert not found' : alertStatusLabel(qa.status)}</span>}
        meta={[
          qa && qa.assignee ? <span>Assigned to <b>{qa.assignee.name}</b></span> : null,
          qa && qa.dueAt ? <span className={overdue ? 'overdue' : ''}>Due {fmtDate(qa.dueAt, moment)}{overdue ? ' · overdue' : ''}</span> : null,
          'Raised ' + by(a.decidedAt)
        ]}
        action={qa ? <Button size='small' href='/alert' target='_blank' rel='noopener noreferrer'>Alert inbox ↗</Button> : null}>
        {a.remarks && <blockquote className='scard-quote' title={a.remarks}>{a.remarks}</blockquote>}
        {qa && qa.open && qa.status === 'ACTION_TAKEN' && <Note tone='ok'><b>Action taken by the assignee.</b> Check the correction, close the query in the alert inbox, then audit the voucher.</Note>}
        {qa && qa.open === false && <Note tone='ok'><b>Audit Query {qa.alertNo} is {(qa.status || 'closed').toLowerCase().replace('_', ' ')}.</b> You can now audit the voucher.</Note>}
        {a.changedSinceAudit && <Note><b>The store has edited the voucher since.</b> Check the correction and audit it.</Note>}
      </StatusCard>
    )
  }
  if (s === 'AUDITED') {
    return (
      <StatusCard tone='audited' icon={<Icon name='check' size={17} />} title='Audited'
        pill={<span className='spill done'>{a.mode === 'BULK' ? 'Bulk · ' + a.batchId : 'Individual'}</span>}
        meta={[by(a.decidedAt)]}>
        {a.remarks && <blockquote className='scard-quote'>{allChecks(item).length ? 'Reason for accepting: ' : ''}{a.remarks}</blockquote>}
      </StatusCard>
    )
  }
  return null
}

// fields = the flag fields set on this line (several allowed).
const LineRow = ({ item, line, idx, fields, canFlag, onToggleLine, onToggleFlag, onPhoto }) => {
  const flag = fields.length > 0
  const sp = line.spare
  const cks = line.checks || []
  if (!sp) {
    return (
      <React.Fragment>
      <tr className={flag ? 'lflag' : ''}>
        <NoCell idx={idx} line={line} count={fields.length} canFlag={canFlag} onToggleLine={onToggleLine} />
        <td colSpan={6}><Missing>Blank line</Missing> <span className='hint'>No spare, unit, quantity or rate.</span></td>
        <td className='chkcell'><LineChecks checks={cks} /></td>
      </tr>
      <FieldRow item={item} line={line} fields={fields} canFlag={canFlag} onToggleFlag={onToggleFlag} />
      </React.Fragment>
    )
  }
  const uomBad = line.uom && sp.masterUom ? line.uom._id !== sp.masterUom._id : false
  const rateChk = cks.find(c => c.code === 'RATE_JUMP')
  return (
    <React.Fragment>
    <tr className={flag ? 'lflag' : ''}>
      <NoCell idx={idx} line={line} count={fields.length} canFlag={canFlag} onToggleLine={onToggleLine} />
      <td className='sparecell'>
        <div className='spare'>
          {sp.photo
            ? <button type='button' className='sthumb' onClick={() => onPhoto(line)} aria-label={'View photo of ' + sp.name}><img src={sp.photo} alt='' loading='lazy' /></button>
            : <span className='sthumb none'>No photo</span>}
          <div style={{ minWidth: 0 }}>
            <div className='sname'><SpareLink spare={sp} /></div>
            <div className='scode'>{[sp.code, isNum(sp.internalCode) ? '#' + sp.internalCode : null].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
      </td>
      <td>
        {line.uom ? <span className={'uom' + (uomBad ? ' bad' : '')}>{line.uom.name}</span> : <Missing />}
        {sp.masterUom
          ? <span className={'ref' + (uomBad ? ' bad' : '')}>{uomBad ? 'Master: ' + sp.masterUom.name : 'matches master'}</span>
          : <span className='ref'>no master unit</span>}
      </td>
      <td className='num'>{qtyFmt(line.qty) || <Missing />}</td>
      <td className='num'>{inr(line.rate) || <Missing />}</td>
      <td className='num'>{inr(line.value) || <Missing>—</Missing>}</td>
      <td className='num lpcell' style={{ textAlign: 'left' }}>
        {item.voucherType === 'PI'
          ? (line.lastPurchase
            ? <LastPurchaseCell lp={line.lastPurchase} rateChk={rateChk} />
            : <span className='notrec'>No prior price on record</span>)
          : (isNum(line.stockBefore)
            ? <span>{qtyFmt(line.stockBefore)} {sp.masterUom ? sp.masterUom.name : ''}
              {isNum(line.qty) && <span className={'ref' + (line.stockBefore - line.qty < 0 ? ' bad' : '')}>after issue {qtyFmt(line.stockBefore - line.qty)}</span>}</span>
            : <span className='notrec'>Not available</span>)}
      </td>
      <td className='chkcell'><LineChecks checks={cks} /></td>
    </tr>
    <FieldRow item={item} line={line} fields={fields} canFlag={canFlag} onToggleFlag={onToggleFlag} />
    </React.Fragment>
  )
}

// Line number + Flag button in the first column, so flagging never needs a
// sideways scroll on a narrow screen. The flagged fields appear in a full-width row below.
const NoCell = ({ idx, line, count, canFlag, onToggleLine }) => (
  <td className='nocell'>
    <div className='nocell-in'>
      <span className='mono'>{idx + 1}</span>
      <button type='button' className='lbtn' aria-pressed={count > 0} disabled={!canFlag} onClick={() => onToggleLine(line)}
        title={count ? 'Clear the ' + count + ' flag' + (count === 1 ? '' : 's') + ' on line ' + (idx + 1) + ' (key ' + (idx + 1) + ')' : 'Flag line ' + (idx + 1) + ' (key ' + (idx + 1) + ')'}>
        {count ? 'Flagged' + (count > 1 ? ' ×' + count : '') : 'Flag'}
      </button>
    </div>
  </td>
)

// Every field of a flagged line can be flagged on its own; several at once. Turning the last one
// off un-flags the line. Remarks for each flag are written in the Raise Audit Query window.
const FieldRow = ({ item, line, fields, canFlag, onToggleFlag }) => (fields.length ? (
  <tr className='lflag reasonrow'>
    <td />
    <td colSpan={7}>
      <div className='reasons'>
        <span className='hint'>Flag what is wrong (any number):</span>
        {flagFields(item.voucherType, line.lineId).map(f => (
          <button type='button' key={f.key} aria-pressed={fields.indexOf(f.key) >= 0} disabled={!canFlag}
            onClick={() => onToggleFlag(line.lineId, f.key)}>{f.label}</button>
        ))}
      </div>
    </td>
  </tr>
) : null)

// Voucher-level fields (vendor, invoice, GRN, job card, attachments…) are flagged from one small
// button with a picker; only the fields already flagged are shown inline, each removable.
const VoucherFlagBar = ({ item, flags, canFlag, onToggleFlag }) => {
  const fields = flagFields(item.voucherType, null)
  const on = fields.filter(f => flags[flagKey(null, f.key)])
  if (!canFlag && !on.length) return null
  const picker = (
    <div className='sax-flagpick'>
      {fields.map(f => (
        <button type='button' key={f.key} aria-pressed={!!flags[flagKey(null, f.key)]} onClick={() => onToggleFlag(null, f.key)}>
          {f.label}
        </button>
      ))}
    </div>
  )
  return (
    <div className='vflagbar'>
      {canFlag && (
        <Popover content={picker} trigger='click' placement='bottomLeft' overlayClassName='sax-flagpop'
          title='Flag a voucher field (any number)'>
          <button type='button' className='vfl-btn'><Icon name='flag' size={13} />Flag voucher field{on.length ? ' · ' + on.length : ''}<Icon name='chevron' size={13} /></button>
        </Popover>
      )}
      {on.map(f => (
        <span key={f.key} className='vfl-on'>
          {f.label}
          {canFlag && <button type='button' aria-label={'Remove flag ' + f.label} onClick={() => onToggleFlag(null, f.key)}>×</button>}
        </span>
      ))}
    </div>
  )
}

// Previous purchase of this spare: rate, the PI (opens in a window), and when and
// where it was bought. Date of purchase is the invoice date; if that PI has none,
// its GRN date is shown and labelled as such. Store is the PI line's store.
// A check's detail text; when the server lists the other vouchers involved (refs, e.g.
// the other PIs with the same vendor invoice), each is shown as a link with its invoice date.
const CheckDetail = ({ check }) => {
  const refs = (check.refs || []).filter(r => r && r.voucherType === 'PI')
  if (!refs.length) return check.detail ? <span>{check.detail}</span> : null
  const head = String(check.detail || '').split(' is also on ')[0]
  return (
    <span>
      {head} is also on{' '}
      {refs.map((r, i) => (
        <React.Fragment key={r.voucherId}>
          {i > 0 ? ', ' : ''}
          <PiLink pi={r} />{' '}
          <span className='hint'>{r.invoiceDate ? 'dated ' + fmtDate(r.invoiceDate, moment) : 'no invoice date'}</span>
        </React.Fragment>
      ))}
    </span>
  )
}

// Checks on one line: the exact check-type name (as in the filter chips) and its detail.
const LineChecks = ({ checks }) => (checks.length
  ? (
    <div className='lchk'>
      {checks.map((c, i) => (
        <div key={i}>
          <span className={'fm ' + sevCls(c.severity)}>{checkLabel(c.code)}</span>
          {c.detail ? <span className='ref'>{c.detail}</span> : null}
        </div>
      ))}
    </div>)
  : <span className='okmark'>✓</span>)

const LastPurchaseCell = ({ lp, rateChk }) => {
  const when = lp.invoiceDate
    ? fmtDate(lp.invoiceDate, moment)
    : (lp.grnDate ? 'GRN ' + fmtDate(lp.grnDate, moment) : null)
  const where = [lp.location && lp.location.name, lp.client && lp.client.name].filter(Boolean).join(' / ')
  return (
    <span>
      {inr(lp.rate)}
      <span className={'ref' + (rateChk ? ' warnc' : '')}>
        <PiLink pi={lp} />{rateChk && rateChk.detail ? ' · ' + rateChk.detail.split(' vs')[0] : ''}
      </span>
      {(lp.invoiceDate !== undefined || lp.location !== undefined) && (
        <span className='ref'>
          {when || <span className='missing'>date missing</span>}
          {' · '}
          {where || <span className='notrec'>store not recorded</span>}
        </span>
      )}
    </span>
  )
}

const History = ({ item, detail, loading }) => {
  if (loading && !detail) return <Spin size='small' />
  const events = (detail && detail.history) || []
  return (
    <ul className='tl'>
      {events.map(e => {
        const m = EVENT_META[e.action] || { label: e.action, color: 'var(--faint)' }
        return (
          <li key={e._id} style={{ '--c': m.color }}>
            <span className='d' />
            <div>
              <div><b>{m.label}</b>{' '}
                {e.mode === 'BULK' && <span className='pill bulk'>Bulk · {e.batchId}</span>}
                {e.mode === 'INDIVIDUAL' && <span className='pill bulk'>Individual</span>}
              </div>
              {e.alertNo && <div className='w'>{e.action === 'QUERY_RAISED' ? 'Audit Query ' + e.alertNo + ' raised' : 'Linked Audit Query ' + e.alertNo}</div>}
              <div className='w'>{fmtDateTime(e.at, moment)} · {e.user ? e.user.name : 'System'}</div>
              {e.remarks ? <div className='w'>{e.action === 'AUDITED' && (e.checksAtTime || []).length ? 'Reason: ' : ''}{e.remarks}</div> : null}
              {e.action === 'AUDITED' && (e.checksAtTime || []).length > 0 && (
                <div className='w'>Accepted with: {Array.from(new Set(e.checksAtTime)).map(checkLabel).join(', ')}</div>
              )}
              {(e.flags || []).length > 0 && (
                <div className='w'>Flagged: {e.flags.map(f => {
                  const n = f.lineId ? (item.lines || []).findIndex(l => l.lineId === f.lineId) : -1
                  const where = !f.lineId ? '' : n >= 0 ? 'Line ' + (n + 1) + ' ' : 'Removed line '
                  return where + flagLabel(item.voucherType, f.lineId, f.field) + (f.remark ? ' (' + f.remark + ')' : '')
                }).join('; ')}</div>
              )}
              {(e.lineVerdicts || []).length > 0 && (
                <div className='w'>Flagged: {e.lineVerdicts.map(lv => reasonLabel(lv.reason) + (lv.note ? ' (' + lv.note + ')' : '')).join(', ')}</div>
              )}
              {(e.queryChecks || []).filter(c => c.included).length > 0 && (
                <div className='w'>In query: {e.queryChecks.filter(c => c.included).map(c => checkLabel(c.code) + (c.remark ? ' (' + c.remark + ')' : '')).join(', ')}</div>
              )}
              {(e.queryChecks || []).filter(c => !c.included).length > 0 && (
                <div className='w'>Removed from query: {e.queryChecks.filter(c => !c.included).map(c => checkLabel(c.code) + ' (' + c.remark + ')').join(', ')}</div>
              )}
            </div>
          </li>
        )
      })}
      <li style={{ '--c': 'var(--faint)' }}>
        <span className='d' />
        <div>
          <div><b>Voucher entered</b></div>
          <div className='w'>{fmtDateTime(item.createdAt, moment) || 'Date not recorded'} · {item.createdBy ? item.createdBy.name : 'Not recorded'}</div>
        </div>
      </li>
    </ul>
  )
}

const Attachments = ({ item, onOpenDoc }) => {
  const docs = item.documents || []
  if (item.voucherType === 'SPI') return <div className='empty'>Spare issues have no attachments.</div>
  if (!docs.length) {
    return item.pendingInvoice
      ? <div className='empty'>Invoice pending: goods received on GRN. The invoice is attached when it arrives.</div>
      : <div className='empty bad'>Invoice is entered but not attached. Ask the store to upload it.</div>
  }
  return (
    <div className='atts'>
      {docs.map((d, i) => isImage(d)
        ? (
          <button type='button' key={d._id || i} className='att' onClick={() => onOpenDoc(d)} title={'Open ' + (d.name || 'attachment')}>
            <span className='pv'><img src={d.url} alt='' loading='lazy' /></span>
            <span className='cap'>{d.name || 'Attachment ' + (i + 1)}</span>
          </button>)
        : (
          <a key={d._id || i} className='att file' href={d.url} target='_blank' rel='noopener noreferrer' title={'Open ' + (d.name || 'attachment')}>
            <span className='pv'>{((d.name || '').split('.').pop() || 'FILE').toUpperCase().slice(0, 4)}</span>
            <span className='cap'>{d.name || 'Attachment ' + (i + 1)} ↗</span>
          </a>))}
    </div>
  )
}

const FocusPanel = ({ links, ...rest }) => (
  <LinkAccess.Provider value={links || { pi: null, spare: null, jobCard: null }}>
    <FocusPanelBody {...rest} />
  </LinkAccess.Provider>
)

const FocusPanelBody = ({
  item, detail, detailLoading, flags, remark, setRemark, remarkRef, busy, perms,
  onToggleLine, onToggleFlag, onAudit, onQuery, onReopen, onNav, onOpenDoc, onPhoto
}) => {
  if (!item) return <div className='focus'><div className='empty' style={{ margin: 18 }}>Pick a voucher from the queue.</div></div>
  const s = statusOf(item)
  const checks = allChecks(item)
  const vChecks = item.checks || []
  const flagged = Object.keys(flags).length
  const canFlag = perms.c && s !== 'AUDITED'
  const fieldsOn = (lineId) => Object.keys(flags).filter(k => flags[k].lineId === lineId).map(k => flags[k].field)
  const hasCrit = checks.some(c => c.severity === 'CRITICAL')
  // Accepting a voucher that has open checks needs a written reason (server enforces it too).
  const needsReason = checks.length > 0
  // An open Audit Query must be closed in the alert inbox before the voucher can be audited (server enforces it too).
  const queryOpen = !!(item.audit && item.audit.queryAlert && item.audit.queryAlert.open)
  const val = voucherValue(item)
  const n = (item.lines || []).length

  return (
    <div className='focus'>
      <div className='fhead'>
        <div className='ftitle'>
          <span className={'type ' + item.voucherType}>{item.voucherType === 'PI' ? 'PURCHASE' : 'ISSUE'}</span>
          <h2>{item.voucherNo}</h2>
          <StatusPill item={item} />
          <span className='sub'>{inr(Math.round(val.sum))}{val.missing ? ' + ' + val.missing + ' line(s) without a value' : ''} · {n} line{n === 1 ? '' : 's'}</span>
          <span className='nav'>
            <button type='button' className='iconbtn' onClick={() => onNav(-1)} aria-label='Previous voucher'>↑ K</button>
            <button type='button' className='iconbtn' onClick={() => onNav(1)} aria-label='Next voucher'>J ↓</button>
          </span>
        </div>
        <Meta item={item} flags={flags} />
        <VoucherFlagBar item={item} flags={flags} canFlag={canFlag} onToggleFlag={onToggleFlag} />
      </div>

      <Banner item={item} detail={detail} />

      <div className='fbody'>
        <div className='fmain'>
          {/* Checks and attachments as two labelled strips: one line each in the usual case. */}
          <div className='strip card'>
            <div className='strip-row'>
              <span className='strip-l'>Checks</span>
              <div className='vflags'>
                {!checks.length && <span className='vf ok'>All checks pass <span>Unit, quantity and rate are consistent with the spare master</span></span>}
                {vChecks.map((c, i) => (
                  <span key={i} className={'vf ' + sevCls(c.severity)}><b>{checkLabel(c.code)}</b><CheckDetail check={c} /></span>
                ))}
                {checks.filter(c => c.lineNo).map((c, i) => (
                  <span key={'l' + i} className={'vf ' + sevCls(c.severity)}><b>{checkLabel(c.code)}</b><span>Line {c.lineNo}{c.detail ? ': ' + c.detail : ''}</span></span>
                ))}
              </div>
            </div>
            <div className='strip-row'>
              <span className='strip-l'>Attachments{(item.documents || []).length > 0 && <kbd title='Press O to open the first attachment'>O</kbd>}</span>
              <Attachments item={item} onOpenDoc={onOpenDoc} />
            </div>
          </div>

          <div>
            <div className='sect-t'>
              <span>Spare lines</span>
              {perms.c && s !== 'AUDITED' && <span className='hint'>Press 1–{Math.min(9, n)} to flag a line</span>}
            </div>
            <div className='tw'>
              <table className='lines'>
                <thead>
                  <tr>
                    <th>#</th><th>Spare</th><th>Unit</th><th className='num'>Qty</th><th className='num'>Rate</th><th className='num'>Value</th>
                    <th>{item.voucherType === 'PI' ? 'Last purchase' : 'Store stock'}</th><th>Checks</th>
                  </tr>
                </thead>
                <tbody>
                  {(item.lines || []).map((ln, i) => (
                    <LineRow key={ln.lineId || i} item={item} line={ln} idx={i} fields={fieldsOn(ln.lineId)}
                      canFlag={canFlag} onToggleLine={onToggleLine} onToggleFlag={onToggleFlag} onPhoto={onPhoto} />
                  ))}
                  <tr className='tot'>
                    <td /><td colSpan={4}>Voucher total{val.missing ? ' (excludes ' + val.missing + ' line(s) without a value)' : ''}</td>
                    <td className='num'>{inr(Math.round(val.sum * 100) / 100)}</td><td colSpan={2} />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {item.voucherType === 'SPI' && item.jobCard && (
            <div>
              <div className='sect-t'>Job card</div>
              <div className='ctx'>
                <div><b className='mono'><JobCardLink jobCard={item.jobCard} /></b>{item.jobCard.asset ? ' · ' + item.jobCard.asset.assetCode + (item.jobCard.asset.model ? ' · ' + item.jobCard.asset.model : '') : ''}</div>
                <div>Complaint: {item.jobCard.complaint ? <q>{item.jobCard.complaint}</q> : <span className='notrec'>Not recorded</span>}</div>
                <div className='hint'>Check that these spares fit this job and this asset.</div>
              </div>
            </div>
          )}

          <div>
            <div className='sect-t'>History</div>
            <History item={item} detail={detail} loading={detailLoading} />
          </div>
        </div>
      </div>

      <div className='decide'>
        {s === 'AUDITED'
          ? (
            <React.Fragment>
              <Input ref={remarkRef} value={remark} onChange={e => setRemark(e.target.value)} placeholder='Why are you reopening this audit? (required)' aria-label='Remark' disabled={!perms.reopen} />
              <Button onClick={onReopen} loading={busy === 'reopen'} disabled={!perms.reopen || !remark.trim()}>Reopen audit</Button>
              {!perms.reopen && <span className='hint'>You need reopen permission.</span>}
            </React.Fragment>)
          : (
            <React.Fragment>
              <Input ref={remarkRef} value={remark} onChange={e => setRemark(e.target.value)} disabled={!perms.c}
                placeholder={flagged
                  ? 'What must the store correct? (carried into the Audit Query)'
                  : needsReason ? 'Why are you accepting it? (required to audit: ' + checks.length + ' open check' + (checks.length === 1 ? '' : 's') + ')' : 'Remark (required for a query)'}
                aria-label={needsReason && !flagged ? 'Reason for accepting' : 'Remark'}
                onPressEnter={() => { if (!flagged && (!needsReason || remark.trim())) onAudit() }} />
              <Button className='sax-btn-query' onClick={onQuery} loading={busy === 'query'} disabled={!perms.c}>
                Raise query <kbd>Q</kbd>
              </Button>
              <Button type='primary' onClick={onAudit} loading={busy === 'audit'} disabled={!perms.c || flagged > 0 || queryOpen || (needsReason && !remark.trim())}>
                {s === 'CHANGED' ? 'Audit again' : 'Mark audited'} <kbd>A</kbd>
              </Button>
              {!perms.c
                ? <span className='hint'>You can view this voucher but not audit it.</span>
                : queryOpen
                  ? (
                    <span className='hint blocker'>
                      Audit Query <b>{item.audit.queryAlert.alertNo}</b> is still open ({alertStatusLabel(item.audit.queryAlert.status)}).
                      Close it in the <a href='/alert' target='_blank' rel='noopener noreferrer'>alert inbox ↗</a> first, then mark this voucher audited.
                    </span>)
                  : flagged
                  ? <span className='hint'>{flagged} item{flagged === 1 ? '' : 's'} flagged: raise a query.</span>
                  : needsReason
                    ? <span className='hint'>{hasCrit ? 'Critical checks are open. ' : ''}A reason is required to accept this voucher; it is recorded with the accepted checks.</span>
                    : null}
            </React.Fragment>)}
      </div>
    </div>
  )
}

export default FocusPanel
